import { FC, SyntheticEvent, type CSSProperties, type Ref } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { ComboBoxComponentProps } from "../../interface";

type Option = Record<string, unknown>;

// `ComboBoxComponentProps` omits these, but every call site relies on them.
// KNOWN BUG, preserved verbatim: `ref` is accepted and then dropped - this component
// renders a plain <div> and never forwards it (currencies.tsx passes `symbolsRef`).
interface ComboBoxExtraProps {
  fields?: { text?: string; value?: string };
  onChange?: (args: { value: unknown; itemData: Record<string, unknown> | string | null }) => void;
  /** Legacy Syncfusion flags, forwarded verbatim to MUI's Autocomplete. */
  allowCustom?: boolean;
  autoSelect?: boolean;
  readOnly?: boolean;
  ref?: Ref<HTMLDivElement>;
}

const ComboBoxComponent: FC<ComboBoxComponentProps & ComboBoxExtraProps> = (props) => {
  const {
    dataSource = [],
    fields = { text: "text", value: "value" },
    value,
    change,
    onChange,
    placeholder,
    isEnabled = true,
    widthInPercentage,
    margin,
    ...rest
  } = props;

  // `dataSource` is declared `unknown[]` on the shared props and documented as "option objects, or a
  // plain string array"; both shapes are carried through to MUI, which is why the label / equality
  // callbacks below already accept either. Entries of any other shape are dropped rather than asserted.
  const isDataSourceEntry = (item: unknown): item is Option | string =>
    typeof item === "string" || (typeof item === "object" && item !== null);
  const options: (Option | string)[] = Array.isArray(dataSource) ? dataSource.filter(isDataSourceEntry) : [];
  const textField = fields.text || "text";
  const valueField = fields.value || "value";

  const resolvedValue: Option | string | null = (() => {
    if (value == null) return null;
    if (typeof value === "object") return value as Option;
    // Plain-string entries never match by field, so they fall through to the `String(value)` free-solo value.
    return (
      options.find((o) => typeof o !== "string" && (o[valueField] === value || o[textField] === value)) ?? String(value)
    );
  })();

  const handleChange = (_: SyntheticEvent, newValue: Option | string | null) => {
    const selectedValue =
      newValue == null ? null : typeof newValue === "string" ? newValue : (newValue[valueField] ?? newValue[textField]);
    if (change) change({ value: selectedValue, itemData: newValue });
    if (onChange) onChange({ value: selectedValue, itemData: newValue });
  };

  const containerStyle: CSSProperties = {
    width: widthInPercentage || "100%",
    margin: margin || undefined,
  };

  return (
    <div style={containerStyle}>
      <Autocomplete
        freeSolo
        options={options}
        value={resolvedValue}
        disabled={!isEnabled}
        getOptionLabel={(option: Option | string) =>
          typeof option === "string" ? option : String(option[textField] ?? "")
        }
        isOptionEqualToValue={(option: Option | string, val: Option | string) => {
          if (typeof option === "string" || typeof val === "string") return option === val;
          return option[valueField] === val[valueField];
        }}
        onChange={handleChange}
        renderInput={(params) => <TextFieldComponent {...params} placeholder={placeholder as string | undefined} />}
        {...rest}
      />
    </div>
  );
};

export default ComboBoxComponent;
